/**
 * Registration page
 */

import { useState, useEffect } from 'react'
import { useNavigate, Link as RouterLink, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Container from '@mui/material/Container'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Link from '@mui/material/Link'
import Alert from '@mui/material/Alert'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import Grid from '@mui/material/Grid'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import { Car, Mail, ArrowLeft } from 'lucide-react'
import GoogleButton from '../../components/auth/GoogleButton'
import { useAuth } from '../../contexts/AuthContext'
import { collection, query, where, getDocs } from 'firebase/firestore'
import { firestore } from '../../config/firebase'
import { registerPageStyles } from './RegisterPage.styles'

type Step = 'choose' | 'email' | 'google-form' | 'invite'

function RegisterPage() {
    const { t } = useTranslation()
    const [searchParams] = useSearchParams()
    const [step, setStep] = useState<Step>('choose')
    const [googleUid, setGoogleUid] = useState('')
    const [firstName, setFirstName] = useState('')
    const [lastName, setLastName] = useState('')
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [addressStreet, setAddressStreet] = useState('')
    const [addressHouseNumber, setAddressHouseNumber] = useState('')
    const [addressCity, setAddressCity] = useState('')
    const [addressZip, setAddressZip] = useState('')
    const [marketingConsent, setMarketingConsent] = useState(false)
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)
    const [inviteToken, setInviteToken] = useState<string | null>(null)
    const [inviteRole, setInviteRole] = useState<string>('')

    const { register, initiateGoogleSignIn, completeRegistration, registerWithToken, registerWithTokenViaGoogle } = useAuth()

    useEffect(() => {
        const token = searchParams.get('token')
        if (!token) return
        setInviteToken(token)
        setStep('invite')
        setLoading(true)
        const q = query(collection(firestore, 'user'), where('token', '==', token))
        getDocs(q).then((snapshot) => {
            if (snapshot.empty) {
                setError(t('auth.register.inviteNotFound'))
                return
            }
            const data = snapshot.docs[0].data()
            if (data.status !== 'pending') {
                setError(t('auth.register.inviteUsed'))
                return
            }
            if (data.firstName) setFirstName(data.firstName)
            if (data.lastName) setLastName(data.lastName)
            if (data.email) setEmail(data.email)
            if (data.role) setInviteRole(data.role)
        }).catch(() => {
            setError(t('auth.register.inviteLoadFailed'))
        }).finally(() => setLoading(false))
    }, [searchParams, t])
    const navigate = useNavigate()

    const handleEmailSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setError('')

        if (password !== confirmPassword) {
            setError(t('errors.passwordsDoNotMatch'))
            return
        }
        if (password.length < 6) {
            setError(t('errors.passwordTooShort'))
            return
        }

        setLoading(true)
        try {
            await register(email, password, firstName, lastName, {
                street: addressStreet,
                houseNumber: addressHouseNumber,
                city: addressCity,
                zip: addressZip,
            }, marketingConsent)
            navigate('/app', { replace: true })
        } catch (err) {
            setError(t('auth.register.failed'))
        } finally {
            setLoading(false)
        }
    }

    const handleGoogleClick = async () => {
        setError('')
        setLoading(true)
        try {
            const result = await initiateGoogleSignIn()

            // Uživatel už je registrovaný — přesměrovat přímo do aplikace
            if (!result.isNewUser) {
                navigate('/app', { replace: true })
                return
            }

            // Nový uživatel — předvyplnit formulář z Google dat
            const parts = (result.displayName || '').split(' ')
            setFirstName(parts[0] || '')
            setLastName(parts.slice(1).join(' '))
            setEmail(result.email || '')
            setGoogleUid(result.uid)
            setStep('google-form')
        } catch (err) {
            setError(t('auth.googleLoginFailed'))
        } finally {
            setLoading(false)
        }
    }

    const handleGoogleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setError('')
        setLoading(true)
        try {
            await completeRegistration(googleUid, firstName, lastName, email, {
                street: addressStreet,
                houseNumber: addressHouseNumber,
                city: addressCity,
                zip: addressZip,
            }, marketingConsent)
            navigate('/app', { replace: true })
        } catch (err) {
            console.error('[RegisterPage] completeRegistration failed:', err)
            setError(err instanceof Error ? err.message : t('auth.register.failed'))
        } finally {
            setLoading(false)
        }
    }

    const handleInviteGoogleClick = async () => {
        setError('')
        setLoading(true)
        try {
            await registerWithTokenViaGoogle(inviteToken!, firstName, lastName, marketingConsent)
            navigate('/app', { replace: true })
        } catch (err) {
            setError(err instanceof Error ? err.message : t('auth.googleLoginFailed'))
        } finally {
            setLoading(false)
        }
    }

    const handleInviteSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setError('')
        if (password !== confirmPassword) {
            setError(t('errors.passwordsDoNotMatch'))
            return
        }
        if (password.length < 6) {
            setError(t('errors.passwordTooShort'))
            return
        }
        setLoading(true)
        try {
            await registerWithToken(inviteToken!, email, password, firstName, lastName, marketingConsent)
            navigate('/app', { replace: true })
        } catch (err) {
            console.error(err)
            setError(err instanceof Error ? err.message : t('auth.register.failed'))
        } finally {
            setLoading(false)
        }
    }

    const logo = (
        <Box sx={registerPageStyles.logoWrapper}>
            <Box sx={registerPageStyles.logoIcon}>
                <Car color="white" size={28} />
            </Box>
            <Typography variant="h5" fontWeight={700} textAlign="center">
                {step === 'google-form' || step === 'invite' ? t('auth.register.completeTitle') : t('auth.register.title')}
            </Typography>
        </Box>
    )

    return (
        <Container maxWidth="xs" sx={registerPageStyles.container}>
            {logo}

            <Paper elevation={0} sx={registerPageStyles.paper}>
                {error && (
                    <Alert severity="error" sx={registerPageStyles.errorAlert}>
                        {error}
                    </Alert>
                )}

                {/* ─── Krok 1: Výběr metody ─────────────────────────── */}
                {step === 'choose' && (
                    <Box sx={registerPageStyles.chooseStep}>
                        <Typography variant="body2" color="text.secondary" textAlign="center" mb={0.5}>
                            {t('auth.register.chooseMethod')}
                        </Typography>

                        <Button
                            variant="outlined"
                            size="large"
                            fullWidth
                            startIcon={<Mail size={20} />}
                            onClick={() => setStep('email')}
                            sx={registerPageStyles.emailMethodButton}
                        >
                            {t('auth.register.byEmail')}
                        </Button>

                        <GoogleButton
                            onClick={handleGoogleClick}
                            disabled={loading}
                            label={t('auth.register.byGoogle')}
                        />

                        <Divider sx={registerPageStyles.chooseDivider} />

                        <Typography variant="body2" color="text.secondary" textAlign="center">
                            {t('auth.register.hasAccount')}{' '}
                            <Link component={RouterLink} to="/login" fontWeight={600}>
                                {t('auth.register.loginLink')}
                            </Link>
                        </Typography>
                    </Box>
                )}

                {/* ─── Krok 2a: Registrace e-mailem ────────────────── */}
                {step === 'email' && (
                    <Box component="form" onSubmit={handleEmailSubmit} sx={registerPageStyles.formStep}>
                        <Box sx={registerPageStyles.backRow}>
                            <IconButton size="small" onClick={() => { setStep('choose'); setError('') }}>
                                <ArrowLeft size={20} />
                            </IconButton>
                            <Typography variant="body2" color="text.secondary">{t('auth.register.byEmail')}</Typography>
                        </Box>

                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label={t('common.firstName')}
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    required
                                    autoComplete="given-name"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label={t('common.lastName')}
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                    required
                                    autoComplete="family-name"
                                />
                            </Grid>
                        </Grid>

                        <TextField
                            fullWidth
                            label={t('common.email')}
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            autoComplete="email"
                        />

                        <Grid container spacing={2}>
                            <Grid item xs={8}>
                                <TextField
                                    fullWidth
                                    label={t('common.street')}
                                    value={addressStreet}
                                    onChange={(e) => setAddressStreet(e.target.value)}
                                    required
                                    autoComplete="address-line1"
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    fullWidth
                                    label={t('common.houseNumber')}
                                    value={addressHouseNumber}
                                    onChange={(e) => setAddressHouseNumber(e.target.value)}
                                    required
                                />
                            </Grid>
                            <Grid item xs={8}>
                                <TextField
                                    fullWidth
                                    label={t('common.city')}
                                    value={addressCity}
                                    onChange={(e) => setAddressCity(e.target.value)}
                                    required
                                    autoComplete="address-level2"
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    fullWidth
                                    label={t('common.zip')}
                                    value={addressZip}
                                    onChange={(e) => setAddressZip(e.target.value)}
                                    required
                                    autoComplete="postal-code"
                                />
                            </Grid>
                        </Grid>

                        <TextField
                            fullWidth
                            label={t('common.password')}
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            autoComplete="new-password"
                        />
                        <TextField
                            fullWidth
                            label={t('auth.register.confirmPassword')}
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            autoComplete="new-password"
                        />

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={marketingConsent}
                                    onChange={(e) => setMarketingConsent(e.target.checked)}
                                    size="small"
                                />
                            }
                            label={
                                <Typography variant="body2" color="text.secondary">
                                    {t('auth.register.marketingConsent')}
                                </Typography>
                            }
                        />

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            size="large"
                            disabled={loading}
                        >
                            {loading ? t('common.loading') : t('auth.register.button')}
                        </Button>

                        <Typography variant="body2" color="text.secondary" textAlign="center">
                            {t('auth.register.hasAccount')}{' '}
                            <Link component={RouterLink} to="/login" fontWeight={600}>
                                {t('auth.register.loginLink')}
                            </Link>
                        </Typography>
                    </Box>
                )}

                {/* ─── Krok 2b: Dokončení Google registrace ────────── */}
                {step === 'google-form' && (
                    <Box component="form" onSubmit={handleGoogleSubmit} sx={registerPageStyles.formStep}>
                        <Typography variant="body2" color="text.secondary" mb={0.5}>
                            {t('auth.register.checkAndFillDetails')}
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label={t('common.firstName')}
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    required
                                    autoComplete="given-name"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label={t('common.lastName')}
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                    required
                                    autoComplete="family-name"
                                />
                            </Grid>
                        </Grid>

                        <TextField
                            fullWidth
                            label={t('common.email')}
                            type="email"
                            value={email}
                            disabled
                            helperText={t('auth.register.emailFromGoogle')}
                        />

                        <Grid container spacing={2}>
                            <Grid item xs={8}>
                                <TextField
                                    fullWidth
                                    label={t('common.street')}
                                    value={addressStreet}
                                    onChange={(e) => setAddressStreet(e.target.value)}
                                    required
                                    autoComplete="address-line1"
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    fullWidth
                                    label={t('common.houseNumber')}
                                    value={addressHouseNumber}
                                    onChange={(e) => setAddressHouseNumber(e.target.value)}
                                    required
                                />
                            </Grid>
                            <Grid item xs={8}>
                                <TextField
                                    fullWidth
                                    label={t('common.city')}
                                    value={addressCity}
                                    onChange={(e) => setAddressCity(e.target.value)}
                                    required
                                    autoComplete="address-level2"
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    fullWidth
                                    label={t('common.zip')}
                                    value={addressZip}
                                    onChange={(e) => setAddressZip(e.target.value)}
                                    required
                                    autoComplete="postal-code"
                                />
                            </Grid>
                        </Grid>

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={marketingConsent}
                                    onChange={(e) => setMarketingConsent(e.target.checked)}
                                    size="small"
                                />
                            }
                            label={
                                <Typography variant="body2" color="text.secondary">
                                    {t('auth.register.marketingConsentOptional')}
                                </Typography>
                            }
                        />

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            size="large"
                            disabled={loading}
                        >
                            {loading ? t('common.loading') : t('auth.register.completeTitle')}
                        </Button>
                    </Box>
                )}

                {/* ─── Krok pozvánka: Dokončení registrace přes token ── */}
                {step === 'invite' && (
                    <Box component="form" onSubmit={handleInviteSubmit} sx={registerPageStyles.formStep}>
                        <Typography variant="body2" color="text.secondary" mb={0.5}>
                            {t('auth.register.checkAndFillDetails')}
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label={t('common.firstName')}
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    required
                                    autoComplete="given-name"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label={t('common.lastName')}
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                    required
                                    autoComplete="family-name"
                                />
                            </Grid>
                        </Grid>

                        <TextField
                            fullWidth
                            label={t('common.email')}
                            type="email"
                            value={email}
                            disabled
                            helperText={t('auth.register.emailFromAdmin')}
                        />

                        <GoogleButton
                            onClick={handleInviteGoogleClick}
                            disabled={loading}
                            label={t('auth.register.continueWithGoogle')}
                        />

                        <Divider>{t('auth.register.orWithPassword')}</Divider>

                        <TextField
                            fullWidth
                            label={t('common.password')}
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            autoComplete="new-password"
                        />
                        <TextField
                            fullWidth
                            label={t('auth.register.confirmPassword')}
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            autoComplete="new-password"
                        />

                        {inviteRole === 'customer' && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={marketingConsent}
                                        onChange={(e) => setMarketingConsent(e.target.checked)}
                                        size="small"
                                    />
                                }
                                label={
                                    <Typography variant="body2" color="text.secondary">
                                        {t('auth.register.marketingConsentOptional')}
                                    </Typography>
                                }
                            />
                        )}

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            size="large"
                            disabled={loading}
                        >
                            {loading ? t('common.loading') : t('auth.register.completeTitle')}
                        </Button>
                    </Box>
                )}
            </Paper>
        </Container>
    )
}

export default RegisterPage
